import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { withImageFallback } from "@/utils/images";

export default function CartDrawer() {
  const navigate = useNavigate();
  const { items, isOpen, closeCart, updateQuantity, removeItem, totalQuantity, subtotal } = useCart();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        aria-label="關閉購物車"
        className="absolute inset-0 bg-[#0f1f18]/50 backdrop-blur-[1px]"
        onClick={closeCart}
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-[#E8E0D6] px-5 py-4 md:px-6">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1B3D2A]/10">
              <i className="ri-shopping-cart-2-line text-[#1B3D2A]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#1B3D2A]">購物車</h2>
              <p className="text-xs text-[#9A8A7A]">{totalQuantity} 件商品</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeCart}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#5A6B5A] hover:bg-[#FAF8F5]"
            aria-label="關閉"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FAF8F5]">
                <i className="ri-shopping-cart-line text-2xl text-[#9A8A7A]" />
              </div>
              <p className="text-sm font-semibold text-[#1B3D2A]">購物車目前是空的</p>
              <p className="mt-1 text-xs text-[#9A8A7A]">將銷售型商品加入購物車後會顯示在這裡</p>
              <button
                type="button"
                onClick={() => {
                  closeCart();
                  navigate("/products");
                }}
                className="mt-5 rounded-xl bg-[#1B3D2A] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#143026]"
              >
                去逛逛產品
              </button>
            </div>
          ) : (
            <ul className="flex flex-col gap-4">
              {items.map((item) => (
                <li key={item.key} className="flex gap-3 rounded-xl border border-[#E8E0D6] p-3">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-[#FAF8F5]">
                    <img src={withImageFallback(item.image)} alt={item.name} className="h-full w-full object-cover object-top" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#1B3D2A]">{item.name}</p>
                        {item.variantLabel && <p className="mt-0.5 text-xs text-[#9A8A7A]">{item.variantLabel}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.key)}
                        className="shrink-0 text-[#9A8A7A] hover:text-[#c3423f]"
                        aria-label={`移除 ${item.name}`}
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 rounded-full border border-[#E8E0D6]">
                        <button
                          type="button"
                          className="flex h-7 w-7 items-center justify-center text-[#5A6B5A] hover:text-[#1B3D2A] disabled:opacity-40"
                          onClick={() => updateQuantity(item.key, item.quantity - 1)}
                          aria-label="減少數量"
                        >
                          <i className="ri-subtract-line text-xs" />
                        </button>
                        <span className="w-6 text-center text-xs font-bold text-[#1B3D2A]">{item.quantity}</span>
                        <button
                          type="button"
                          className="flex h-7 w-7 items-center justify-center text-[#5A6B5A] hover:text-[#1B3D2A] disabled:opacity-40"
                          onClick={() => updateQuantity(item.key, item.quantity + 1)}
                          disabled={item.maxStock !== null && item.quantity >= item.maxStock}
                          aria-label="增加數量"
                        >
                          <i className="ri-add-line text-xs" />
                        </button>
                      </div>
                      <span className="text-sm font-bold text-[#1B3D2A]">
                        NT$ {(item.unitPrice * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E8E0D6] px-5 py-4 md:px-6">
            <div className="mb-3 flex items-center justify-between text-sm">
              <span className="text-[#5A6B5A]">小計</span>
              <span className="text-lg font-bold text-[#1B3D2A]">NT$ {subtotal.toLocaleString()}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                closeCart();
                navigate("/cart");
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3 text-sm font-bold text-white hover:bg-[#143026]"
            >
              查看購物車
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
